import { cloneDeep, isEmpty, isNil } from 'lodash';
import * as yup from 'yup';
import { i18nKeyType } from '../../../../i18n';
import { getLocalizedText } from '../../../../i18n/i18n.utils';
import { EnumType } from '../../../../util/enum';
import { logger } from '../../../../util/logger';
import { QNear, Waypoint } from '../../../../util/machine-variable.utils';
import {
  MotionPoint,
  PartUtil,
  WorkPiecePosition,
} from '../../../../util/part.util';
import { Pose } from '../../../../util/pose.util';
import { PickerOption } from '../component/widget/widget.types';
import { MoveToPointProps } from '../hook/move-to-point.hooks';
import {
  ApplicationDefaultParameterSet,
  FineTuneAngles,
  FineTuneProps,
  FineTuneSettings,
  Flow,
  FlowItem,
  FlowItemArrayType,
  FlowItemAttributes,
  FlowItemForm,
  FlowItemMetadata,
  FlowItemParameter,
  FlowItemParameterAdjustmentMetadata,
  FlowItemParameterMetadata,
  FlowItemParameterRef,
  FlowItemParameterRefAdjustmentMetadata,
  FlowItemRef,
  FlowItemSectionParent,
  FlowItemSharedParameterRef,
  FlowItemText,
  FlowSelectedItemInfo,
  ParametersForm,
  SystemUnit,
  WidgetType,
} from '../model/flow.types';
import {
  instanceOfFlowItem,
  instanceOfFlowItemSection,
} from '../util/flow-item.util';
import {
  buildYupParameterSchema,
  convertParameterValueToUOM,
  getFlowItemParameterSchemaRange,
  getParameterValue,
} from '../util/flow-parameter.util';
import {
  ConvertMotionPointToFlowItemParams,
  ConvertMotionPointsToFlowItemSectionParams,
  ConvertMotionPointsToFlowItemSectionsParams,
  FlowItemService,
  GetYupSchemaForItemContext,
} from './service.types';

interface InternalCache<T> {
  [key: string]: T;
}

abstract class AbstractFlowItemService<
  FlowConfig,
  SectionType extends EnumType = EnumType,
> implements FlowItemService<FlowConfig, SectionType>
{
  protected applicationDefaultParameterSetItemRef: FlowItemRef | undefined;
  protected applicationDefaultParameterSet:
    | ApplicationDefaultParameterSet
    | undefined;

  private flowItemMetadataCache: InternalCache<FlowItemMetadata> = {};
  private flowItemParameterMetadataCache: InternalCache<FlowItemParameterMetadata> =
    {};

  constructor(
    protected items: FlowItemMetadata[],
    protected defaultItemRef: FlowItemRef,
    protected parameterAdjustmentMetadata?: FlowItemParameterRefAdjustmentMetadata
  ) {}

  public abstract canConvertFlowItemToMultipass(item: FlowItem): boolean;

  public abstract canConvertFlowItemToStitch(item: FlowItem): boolean;

  public abstract convertFlowItemsToMotionPoints(
    items: FlowItem[]
  ): MotionPoint[];

  public abstract isMultipassSupportedMotionType(item: FlowItem): boolean;

  public abstract isStitchSupportedMotionType(item: FlowItem): boolean;

  public abstract convertMotionPointsToFlowItemSections(
    params: ConvertMotionPointsToFlowItemSectionsParams<SectionType>
  ): FlowItem[];

  public abstract convertMotionPointsToFlowItemSection(
    params: ConvertMotionPointsToFlowItemSectionParams<SectionType>
  ): FlowItem[];

  public abstract convertMotionPointToFlowItem(
    params: ConvertMotionPointToFlowItemParams
  ): FlowItem;

  public preSave(
    config: FlowConfig,
    items: FlowItem[],
    clone = true
  ): FlowItem[] {
    let clonedItems = clone ? cloneDeep(items) : items;

    clonedItems = this.recalculateParameterValues(config, clonedItems);

    return clonedItems;
  }

  public setParameterValue<T>(
    item: FlowItem,
    ref: FlowItemParameterRef,
    value: T
  ): FlowItem {
    item.parameters = item.parameters.map((p) => {
      if (p.ref === ref) {
        return {
          ...p,
          value,
        };
      }
      return p;
    });

    return item;
  }

  public getParametersMetadata(ref: FlowItemRef): FlowItemParameterMetadata[] {
    const item = this.getItemMetadata(ref);
    if (item) {
      return item.availableParameters;
    }
    return [];
  }

  public getParametersMetadataMap(
    ref: FlowItemRef
  ): Record<FlowItemParameterRef, FlowItemParameterMetadata> {
    return this.getParametersMetadata(ref).reduce(
      (acc, param) => {
        acc[param.ref] = param;
        return acc;
      },
      {} as Record<FlowItemParameterRef, FlowItemParameterMetadata>
    );
  }

  public getParametersMetadataByTags(
    ref: FlowItemRef,
    form: ParametersForm,
    tags: string[] = [],
    filterByTagsOnly = false
  ): FlowItemParameterMetadata[] {
    const item = this.getItemMetadata(ref);

    if (item) {
      if (isEmpty(tags)) {
        return item.availableParameters;
      } else {
        return item.availableParameters.filter((param) => {
          if (param.tags || filterByTagsOnly) {
            if (param.tagCondition) {
              return param.tagCondition(tags, form);
            } else {
              const matchingTags = tags.filter((t) => param.tags?.includes(t));
              return matchingTags.length > 0;
            }
          } else {
            return true;
          }
        });
      }
    }
    return [];
  }

  public abstract getRequiredParametersForTeaching(
    ref: FlowItemRef
  ): FlowItemParameterMetadata[];

  public abstract getStartOfSequenceIndexFromFlowItem(
    flowItem: FlowItem,
    flowItems: FlowItem[]
  ): number | undefined;

  public abstract getEndOfSequenceIndexFromFlowItem(
    flowItem: FlowItem,
    flowItems: FlowItem[]
  ): number | undefined;

  public abstract getEndOfSequenceItemFromFlowItem(
    flowItem: FlowItem,
    flowItems: FlowItem[]
  ): FlowItem | undefined;

  public getItemMetadata(ref: FlowItemRef): FlowItemMetadata | undefined {
    const key = ref;

    let metadata: FlowItemMetadata | undefined =
      this.flowItemMetadataCache[key];

    if (metadata === undefined) {
      metadata = this.items.find((i) => i.ref === ref);

      if (metadata) {
        this.flowItemMetadataCache[key] = metadata;
      }
    }

    return metadata;
  }

  public getItemsMetadata(): FlowItemMetadata[] {
    return this.items;
  }

  public getParameterMetadata(
    itemRef: FlowItemRef,
    parameterRef: FlowItemParameterRef
  ): FlowItemParameterMetadata | undefined {
    const key = `${itemRef}-${parameterRef}`;

    let metadata: FlowItemParameterMetadata | undefined =
      this.flowItemParameterMetadataCache[key];

    if (metadata === undefined) {
      metadata = this.getParametersMetadata(itemRef)?.find(
        (p) => p.ref === parameterRef
      );

      if (metadata) {
        this.flowItemParameterMetadataCache[key] = metadata;
      }
    }

    return metadata;
  }

  public getDefaultItemRef(): string {
    return this.defaultItemRef;
  }

  public getDefaultItemForRef<
    FlowItemAttributesType extends FlowItemAttributes,
  >(
    ref: FlowItemRef,
    item?: FlowItem<FlowItemRef, FlowItemAttributesType>
  ): FlowItem<FlowItemRef, FlowItemAttributesType> {
    const itemMetadata = this.getItemMetadata(ref);

    const defaultParameters =
      itemMetadata?.availableParameters.map(
        (p): FlowItemParameter => ({
          ref: p.ref,
          value: p.value.default,
          baseValue: p.value.default,
        })
      ) ?? [];

    let baseItem: FlowItem<FlowItemRef, FlowItemAttributesType> = {
      name: ref,
      ref,
      parameters: defaultParameters,
    };

    if (
      this.applicationDefaultParameterSet &&
      ref === this.applicationDefaultParameterSetItemRef
    ) {
      baseItem = this.applyApplicationDefaultParameterSetToFlowItem(
        baseItem,
        this.applicationDefaultParameterSet
      );
    }

    if (item) {
      return this.mergeItems(baseItem, item);
    }

    return baseItem;
  }

  public mergeItems<FlowItemAttributesType extends FlowItemAttributes>(
    defaultItem: FlowItem<FlowItemRef, FlowItemAttributesType>,
    item: FlowItem<FlowItemRef, FlowItemAttributesType>
  ): FlowItem<FlowItemRef, FlowItemAttributesType> {
    const merged: FlowItem<FlowItemRef, FlowItemAttributesType> = {
      ...defaultItem,
      ...item,
    };

    const mergedParameters: FlowItemParameter[] = [...item.parameters];

    // merge any new parameters from the default item that don't exist
    // on our new item
    for (const baseParameter of defaultItem.parameters) {
      const exist = mergedParameters.find((p) => p.ref === baseParameter.ref);

      if (!exist) {
        mergedParameters.push(baseParameter);
      }
    }

    merged.parameters = mergedParameters;

    return merged;
  }

  public applyApplicationDefaultParameterSetToFlowItem<
    FlowItemAttributesType extends FlowItemAttributes,
  >(
    item: FlowItem<FlowItemRef, FlowItemAttributesType>,
    parameterSet: ApplicationDefaultParameterSet
  ): FlowItem<FlowItemRef, FlowItemAttributesType> {
    if (parameterSet?.data) {
      for (const defaultParameter of parameterSet.data.parameters) {
        for (const itemParameter of item.parameters) {
          if (
            defaultParameter.ref === itemParameter.ref &&
            defaultParameter.value !== undefined
          ) {
            itemParameter.value = defaultParameter.value;
          }
        }
      }
    }

    return item;
  }

  public convertFlowItemToForm(
    item: FlowItem,
    system: SystemUnit,
    skipDefaultParams?: boolean
  ): FlowItemForm {
    const mergedItem = skipDefaultParams
      ? item
      : this.getDefaultItemForRef(item.ref, item);

    return {
      ...mergedItem,
      parameters: this.mapParametersToFormObject(
        mergedItem.ref,
        mergedItem.parameters,
        system
      ),
    };
  }

  public convertFormToFlowItem(
    form: FlowItemForm,
    system: SystemUnit
  ): FlowItem {
    return {
      ...form,
      parameters: Object.keys(form.parameters).map((k): FlowItemParameter => {
        const ref = k as FlowItemParameterRef;

        const { value, baseValue } = form.parameters[k];

        const convertedValue = this.processFormValueForFlowItem({
          itemRef: form.ref,
          parameterRef: ref,
          value,
          system,
        });

        const convertedBaseValue = this.processFormValueForFlowItem({
          itemRef: form.ref,
          parameterRef: ref,
          value: baseValue,
          system,
        });

        return {
          ref,
          value: convertedValue,
          baseValue: convertedBaseValue,
        };
      }),
    };
  }

  public getYupSchemaForItem(
    ref: FlowItemRef,
    system: SystemUnit,
    context?: GetYupSchemaForItemContext,
    parameters?: FlowItemParameterMetadata[]
  ): yup.AnySchema {
    const metadata = this.getItemMetadata(ref);

    const parameterSchemaShape = (
      parameters ??
      metadata?.availableParameters ??
      []
    ).reduce((prev, curr) => {
      const { value, ref } = curr;

      let updatedValue = value;
      let adjustmentMetadata: FlowItemParameterAdjustmentMetadata | undefined =
        undefined;
      if (
        this.parameterAdjustmentMetadata &&
        this.parameterAdjustmentMetadata[curr.ref]
      ) {
        adjustmentMetadata = this.parameterAdjustmentMetadata[curr.ref];
      }

      const range =
        adjustmentMetadata && context
          ? getFlowItemParameterSchemaRange({
              context,
              adjustmentMetadata,
              ref,
            })
          : undefined;

      if (curr.buildValidation && context) {
        updatedValue = curr.buildValidation({
          flow: context.flow,
          form: context.form.parameters,
          value: cloneDeep(value),
          range,
        });
      }

      const schema = buildYupParameterSchema(updatedValue, system, range);

      return {
        ...prev,
        [curr.ref]: yup.object().shape({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          value: schema as any,
        }),
      };
    }, {});

    return yup.object().shape({
      name: yup.string(),
      parameters: yup.object().shape(parameterSchemaShape),
    });
  }

  public generateFlowItemName(ref: FlowItemRef): string {
    const meta = this.getItemMetadata(ref);
    return `${getLocalizedText(meta?.name) ?? ref}`;
  }

  public setApplicationDefaultParameterSet(
    itemRef: FlowItemRef,
    applicationDefaultParameterSet: ApplicationDefaultParameterSet
  ): void {
    this.applicationDefaultParameterSetItemRef = itemRef;
    this.applicationDefaultParameterSet = applicationDefaultParameterSet;
  }

  public getApplicationDefaultParameterSet():
    | ApplicationDefaultParameterSet
    | undefined {
    return this.applicationDefaultParameterSet;
  }

  public isOfType(ref: string, item: FlowItem) {
    return ref === item.ref;
  }

  private mapParametersToFormObject(
    itemRef: FlowItemRef,
    parameters: FlowItemParameter[],
    system: SystemUnit
  ): ParametersForm {
    const form: ParametersForm = {};
    for (const p in parameters) {
      const param = parameters[p];

      // if the param value happens to be null enforce that it is undefined instead
      const originalValue = param.value ?? undefined;
      const convertedValue = this.processItemValueForForm({
        itemRef,
        parameterRef: param.ref,
        value: originalValue,
        system,
      });
      const originalBaseValue = param.baseValue ?? undefined;
      const convertedBaseValue = this.processItemValueForForm({
        itemRef,
        parameterRef: param.ref,
        value: originalBaseValue,
        system,
      });

      form[param.ref] = {
        value: convertedValue ?? undefined,
        originalValue,
        baseValue: convertedBaseValue,
        originalBaseValue,
      };
    }
    return form;
  }

  public hasShiftableItem(items: FlowItem[]): boolean {
    const shiftable = items.find((i) => this.isShiftableItem(i));
    return shiftable ? true : false;
  }

  public shiftFlowItems(
    flowItems: FlowItem[],
    origin: Waypoint,
    newOrigin: Waypoint,
    originQnear: QNear,
    newOriginQnear: QNear,
    rotationAngle: number
  ): FlowItem[] {
    const clonedItems = cloneDeep(flowItems);

    // get all of the items that have a type we can shift
    const itemsToShift = clonedItems.filter((s) => this.isShiftableItem(s));

    // convert waypoint parameters to poses
    const poses = itemsToShift.map((i) => {
      const waypoint = getParameterValue<Waypoint>(
        i,
        FlowItemSharedParameterRef.WAYPOINT
      );
      return PartUtil.convertWaypointToPose(waypoint);
    });

    // get Qnears of all points
    const qnears = itemsToShift.map((i) => {
      return getParameterValue<QNear>(i, FlowItemSharedParameterRef.QNEAR);
    });

    const originAsPose = PartUtil.convertWaypointToPose(origin);
    const newOriginAsPose = PartUtil.convertWaypointToPose(newOrigin);

    const shiftedPose = PartUtil.rotatePart(
      poses,
      originAsPose,
      newOriginAsPose,
      rotationAngle
    );

    const shiftedQnear = PartUtil.translatePartQnears(
      qnears,
      originQnear,
      newOriginQnear
    );

    // remap the shifted points back to the original items
    for (let i = 0; i < shiftedPose.length; i++) {
      const waypoint = PartUtil.convertPoseToWaypoint(shiftedPose[i]);

      this.setParameterValue(
        itemsToShift[i],
        FlowItemSharedParameterRef.WAYPOINT,
        waypoint
      );
    }

    // remap the shifted qnears back to the original items
    for (let i = 0; i < shiftedQnear.length; i++) {
      this.setParameterValue(
        itemsToShift[i],
        FlowItemSharedParameterRef.QNEAR,
        shiftedQnear[i]
      );
    }
    return clonedItems;
  }

  public getFineTuneAngles(fineTuneProps: FineTuneProps): FineTuneAngles {
    const isRadial = fineTuneProps.type === 'radial';

    let desiredAngles: FineTuneAngles = { travelAngle: 0, workAngle: 0 };

    if (fineTuneProps.endPoint) {
      if (isRadial) {
        if (fineTuneProps.midPoint) {
          desiredAngles = PartUtil.getCurrentTorchAngles_radial(
            PartUtil.convertWaypointToPose(fineTuneProps.startPoint),
            PartUtil.convertWaypointToPose(fineTuneProps.midPoint),
            PartUtil.convertWaypointToPose(fineTuneProps.endPoint),
            fineTuneProps.pointToProcess,
            fineTuneProps.workPiecePosition,
            fineTuneProps.workPieceOffset
          );
        }
      } else {
        desiredAngles = PartUtil.getCurrentTorchAngles_linear(
          PartUtil.convertWaypointToPose(fineTuneProps.startPoint),
          PartUtil.convertWaypointToPose(fineTuneProps.endPoint),
          fineTuneProps.pointToProcess as unknown as any,
          fineTuneProps.midPoint
            ? PartUtil.convertWaypointToPose(fineTuneProps.midPoint)
            : undefined,
          fineTuneProps.workPiecePosition,
          fineTuneProps.workPieceOffset
        );
      }
    }

    return desiredAngles;
  }

  public setFineTuneSettingsForFlowItems(
    flow: Flow<FlowConfig>,
    startItem: FlowItem,
    settings: FineTuneSettings
  ): FlowItem[] {
    const contiguous = this.getContiguousItemsForFlowItem(
      startItem,
      flow.items
    );

    const { travelAngle, workAngle, stickout } = settings;

    const currentItemIndex = contiguous.findIndex((c) => c.id === startItem.id);

    const updatedItems: FlowItem[] = [];

    for (let i = currentItemIndex + 1; i < contiguous.length; i++) {
      const nextItem = cloneDeep(contiguous[i]);

      if (stickout !== undefined) {
        this.setParameterValue(
          nextItem,
          FlowItemSharedParameterRef.STICKOUT,
          stickout
        );
      }

      const nextFineTuneProps = this.getFineTuneProps(flow, nextItem);

      const isRadial = nextFineTuneProps.type === 'radial';

      let pose: Pose | undefined;

      if (nextFineTuneProps.endPoint) {
        if (isRadial) {
          if (nextFineTuneProps.midPoint) {
            pose = PartUtil.setDesiredTorchAngles_radial(
              PartUtil.convertWaypointToPose(nextFineTuneProps.startPoint),
              PartUtil.convertWaypointToPose(nextFineTuneProps.midPoint),
              PartUtil.convertWaypointToPose(nextFineTuneProps.endPoint),
              nextFineTuneProps.pointToProcess,
              travelAngle,
              workAngle,
              nextFineTuneProps.workPiecePosition,
              nextFineTuneProps.workPieceOffset
            );
          }
        } else {
          pose = PartUtil.setDesiredTorchAngles_linear(
            PartUtil.convertWaypointToPose(nextFineTuneProps.startPoint),
            PartUtil.convertWaypointToPose(nextFineTuneProps.endPoint),
            nextFineTuneProps.pointToProcess as unknown as any,
            nextFineTuneProps.midPoint
              ? PartUtil.convertWaypointToPose(nextFineTuneProps.midPoint)
              : undefined,
            travelAngle,
            workAngle,
            nextFineTuneProps.workPiecePosition,
            nextFineTuneProps.workPieceOffset
          );
        }

        if (pose !== undefined) {
          this.setParameterValue(
            nextItem,
            FlowItemSharedParameterRef.WAYPOINT,
            PartUtil.convertPoseToWaypoint(pose)
          );
        }
      }

      updatedItems.push(nextItem);
    }
    return updatedItems;
  }

  public getFlowItemPickerLabel(
    flowItem: FlowItem,
    parameterRef: FlowItemParameterRef
  ): string | undefined {
    const option = this.getFlowItemPickerOption(flowItem, parameterRef);

    if (option) {
      return getLocalizedText(option.label as i18nKeyType);
    }
  }

  public getFlowItemPickerOption(
    flowItem: FlowItem,
    parameterRef: FlowItemParameterRef
  ): PickerOption | undefined {
    return this.getFlowItemPickerOptionByValue(
      flowItem,
      parameterRef,
      getParameterValue(flowItem, parameterRef)
    );
  }

  public getFlowItemPickerOptionByValue = (
    flowItem: FlowItem,
    parameterRef: FlowItemParameterRef,
    value: unknown
  ): PickerOption | undefined => {
    const parameterMetadata = this.getParameterMetadata(
      flowItem.ref,
      parameterRef
    );

    if (parameterMetadata) {
      return parameterMetadata.ui?.widgetOptions?.find((o: PickerOption) => {
        return o.value === value;
      });
    }
  };

  public abstract recalculateParameterValues(
    config: FlowConfig,
    items: FlowItem[],
    options?: {
      isDefaultParameterSet?: boolean;
    }
  ): FlowItem[];

  public revertParentSection(params: {
    parentItem: FlowItemSectionParent;
    currentItems: FlowItem[];
  }): FlowItemArrayType[] | undefined {
    const { parentItem, currentItems } = params;

    let clonedItems: FlowItemArrayType[] = cloneDeep(currentItems);

    const startIndex = clonedItems.findIndex(
      (i) => instanceOfFlowItem(i) && i.id === parentItem.id
    );
    const endIndex = clonedItems.findIndex(
      (i) => instanceOfFlowItem(i) && i.id === parentItem.attributes.endItemId
    );

    if (startIndex !== -1 && endIndex !== -1) {
      clonedItems = [
        ...clonedItems.slice(0, startIndex),
        ...parentItem.attributes.originalItems,
        ...clonedItems.slice(endIndex + 1),
      ];
    }

    return clonedItems;
  }

  public abstract buildFlowItemTextArr(
    flowItems: FlowItemArrayType[]
  ): (FlowItemText | undefined)[];

  public abstract isShiftableItem(item: FlowItem): boolean;

  public abstract getFineTuneProps(
    flow: Flow<FlowConfig>,
    item: FlowItem
  ): FineTuneProps;

  public abstract getFlowSelectedItemInfo(
    flow: Flow<unknown>,
    selectedItems: string[]
  ): FlowSelectedItemInfo;

  public abstract getFlowItemSubheading(
    flowItem: FlowItem,
    system?: SystemUnit
  ): string | undefined;

  public abstract getMoveToPointProps(
    flow: Flow<FlowConfig>,
    item?: FlowItem
  ): MoveToPointProps;

  public abstract getApplicationDefaultParameterSetImplicitTags(): Record<
    string,
    unknown
  >;

  public abstract getContiguousItemsForFlowItem(
    item: FlowItem<string>,
    items: FlowItem<string>[]
  ): FlowItem<string>[];

  // The primary action being the main function of the application (weld for welding, cut for cutting, etc.)
  public abstract isSinglePrimaryActionItem(item: FlowItem): boolean;

  public abstract getCopyWithNestedItems(items: FlowItem[]): FlowItem[];

  public handlePasteFlowItems(flowItems: FlowItem[]): FlowItem[] {
    return flowItems.map((item) => {
      item.id = undefined;
      return item;
    });
  }

  public getInferredWorkSurfacePosition(params: {
    item: FlowItem<string>;
    flowItems: FlowItem<string>[];
  }): WorkPiecePosition | undefined {
    const { item, flowItems } = params;
    if (item.attributes?.workSurfacePosition) {
      return item.attributes.workSurfacePosition;
    }

    const isPrimaryActionType = item.ref === this.getDefaultItemRef();
    const isSinglePrimaryActionType = this.isSinglePrimaryActionItem(item);

    // non-primary action items or single items cannot provide a work surface position
    if (!isPrimaryActionType || isSinglePrimaryActionType) {
      return;
    }

    const contiguousItems = this.getContiguousItemsForFlowItem(
      item,
      flowItems
    ).filter((i) => !instanceOfFlowItemSection(i));

    if (contiguousItems.length === 0) {
      logger.warn(
        'cannot infer work surface position. no contiguous items found for item. returning undefined',
        {
          item,
          flowItems,
        }
      );
      return;
    }

    const idx = contiguousItems.findIndex((i) => i.id === item.id);
    const nextIdx = idx + 1;
    const prevIdx = idx - 1;

    const itemWaypoint = getParameterValue<Waypoint>(
      item,
      FlowItemSharedParameterRef.WAYPOINT
    );

    if (!itemWaypoint) {
      logger.error(
        'cannot infer work surface position. item waypoint is undefined. returning undefined',
        {
          item,
          itemId: item.id,
          itemRef: item.ref,
        }
      );
      return;
    }

    if (contiguousItems[nextIdx]) {
      const nextWaypoint = getParameterValue<Waypoint>(
        contiguousItems[nextIdx],
        FlowItemSharedParameterRef.WAYPOINT
      );

      if (!nextWaypoint) {
        logger.error(
          'cannot infer work surface position. next item waypoint is undefined. returning undefined',
          {
            nextItem: contiguousItems[nextIdx],
            nextItemId: contiguousItems[nextIdx].id,
            nextItemRef: contiguousItems[nextIdx].ref,
            currentItem: item,
            currentItemId: item.id,
          }
        );
        return;
      }

      const currentPose = PartUtil.convertWaypointToPose(itemWaypoint);
      const nextPose = PartUtil.convertWaypointToPose(nextWaypoint);

      return PartUtil.inferWorkPiecePosition(currentPose, nextPose);
    } else if (contiguousItems[prevIdx]) {
      const prevWaypoint = getParameterValue<Waypoint>(
        contiguousItems[prevIdx],
        FlowItemSharedParameterRef.WAYPOINT
      );

      if (!prevWaypoint) {
        logger.error(
          'cannot infer work surface position. previous item waypoint is undefined. returning undefined',
          {
            prevItem: contiguousItems[prevIdx],
            prevItemId: contiguousItems[prevIdx].id,
            prevItemRef: contiguousItems[prevIdx].ref,
            currentItem: item,
            currentItemId: item.id,
          }
        );
        return;
      }

      const currentPose = PartUtil.convertWaypointToPose(itemWaypoint);
      const prevPose = PartUtil.convertWaypointToPose(prevWaypoint);

      return PartUtil.inferWorkPiecePosition(prevPose, currentPose);
    }
  }

  private processFormValueForFlowItem(params: {
    itemRef: FlowItemRef;
    parameterRef: FlowItemParameterRef;
    value: unknown;
    system: SystemUnit;
  }): unknown {
    const { itemRef, parameterRef, value, system } = params;
    const metadata = this.getParameterMetadata(itemRef, parameterRef);

    if (metadata?.value.type === 'number' && !isNil(value)) {
      const convertedValue = Number(value);
      if (!isFinite(convertedValue)) {
        logger.error('failed to convert value to number', {
          itemRef,
          parameterRef,
          value,
        });
        return value;
      }

      return Number(
        convertParameterValueToUOM(
          metadata.value,
          system,
          value as number,
          true
        )
      );
    }

    return value;
  }

  private processItemValueForForm(params: {
    itemRef: FlowItemRef;
    parameterRef: FlowItemParameterRef;
    value: unknown;
    system: SystemUnit;
  }): unknown {
    const { itemRef, parameterRef, value, system } = params;
    const metadata = this.getParameterMetadata(itemRef, parameterRef);
    let convertedValue: any = value;

    if (
      metadata &&
      metadata.value.uom &&
      metadata.value.type === 'number' &&
      convertedValue !== undefined
    ) {
      convertedValue = Number(convertedValue);

      if (!isFinite(convertedValue)) {
        logger.error('failed to convert value to number', {
          itemRef,
          parameterRef,
          value,
        });
        return value;
      }

      convertedValue = convertParameterValueToUOM(
        metadata.value,
        system,
        convertedValue,
        false
      );
    }

    if (
      metadata?.ui?.widget &&
      metadata.ui.widget === WidgetType.NUMBER &&
      convertedValue != undefined
    ) {
      convertedValue = String(convertedValue);
    }

    return convertedValue;
  }
}

export { AbstractFlowItemService };
